/* eslint-disable prettier/prettier -- behoud de afgesproken witregels binnen conditionals. */
import { TextureLoader } from 'three'

const textureLoader = new TextureLoader()

export const isAbortError = (error) => error?.name === 'AbortError'

export function createAbortError() {
  return new DOMException('Asset request aborted.', 'AbortError')
}

export function throwIfAborted(signal) {
  if (signal?.aborted) {

    throw createAbortError()

  }
}

export function createAssetRequestScope() {
  const controllers = new Set()

  let active = true

  return {
    createController() {
      const controller = new AbortController()

      if (active) {

        controllers.add(controller)

      } else {

        controller.abort()

      }

      return controller
    },
    release(controller) {
      controllers.delete(controller)
    },
    abort() {
      active = false

      const pendingControllers = [...controllers]

      // de eigenaar stopt ook requests uit de afzonderlijke R3F-root direct.
      controllers.clear()

      pendingControllers.forEach((controller) => controller.abort())
    },
  }
}

async function fetchAsset(url, responseType, signal) {
  throwIfAborted(signal)

  // iedere viewer krijgt een eigen request dat onafhankelijk afgebroken kan worden.
  const response = await fetch(url, { signal })

  if (!response.ok) {

    throw new Error(`Asset request failed (${response.status}): ${url}`)

  }

  const data = await response[responseType]()

  throwIfAborted(signal)

  return data
}

export const fetchAssetBuffer = (url, signal) =>
  fetchAsset(url, 'arrayBuffer', signal)

export const fetchAssetText = (url, signal) => fetchAsset(url, 'text', signal)

export const fetchAssetBlob = (url, signal) => fetchAsset(url, 'blob', signal)

export async function loadImageTexture(url, signal) {
  const blob = await fetchAssetBlob(url, signal)

  throwIfAborted(signal)

  const objectURL = URL.createObjectURL(blob)

  return new Promise((resolve, reject) => {
    let texture

    let settled = false

    const cleanup = () => {
      signal?.removeEventListener('abort', handleAbort)
      URL.revokeObjectURL(objectURL)
    }

    const handleAbort = () => {
      if (settled) {

        return

      }

      settled = true

      cleanup()
      texture?.dispose()
      reject(createAbortError())
    }

    signal?.addEventListener('abort', handleAbort, { once: true })

    if (signal?.aborted) {

      handleAbort()
      return

    }

    try {

      // behoud de bestaande HTMLImageElement-decoder en texturedefaults van three.
      texture = textureLoader.load(
        objectURL,
        (loadedTexture) => {
          if (settled || signal?.aborted) {

            loadedTexture.dispose()
            handleAbort()
            return

          }

          settled = true

          cleanup()
          resolve(loadedTexture)
        },
        undefined,
        (error) => {
          if (settled) {

            return

          }

          settled = true

          cleanup()
          texture?.dispose()
          reject(error)
        },
      )

    } catch (error) {

      if (!settled) {

        settled = true

        cleanup()
        texture?.dispose()
        reject(error)

      }

    }
  })
}
